import React, { FC } from 'react';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import { Box, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import InvoiceLateIcon from '~Assets/icons/invoice_late.svg';
import { useButton, useI18n } from '~Hooks';

const HandyWrapper = styled(Box)`
  width: 100vw;
  height: 100%;
  background: var(--colorBlueGray1200);
  position: relative;
`;

const ModalWrapper = styled(Box)`
  position: relative;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  animation: fadeIn 1s ease;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContentBox = styled(Box)`
  max-width: 600px;
  text-align: center;
`;

const UnderstoodButton = styled(Button)`
  min-width: 160px;
`;

interface Props {
  onComplete: () => void;
  isStripeLicense?: boolean; // Whether the warning is due to an unpaid Stripe license, used to determine the text in the modal
}

/** Modal component that will be displayed if the Venue's billing account has receieved a warning from Superadmin */
const InvoiceWarning: FC<Props> = ({ onComplete, isStripeLicense }) => {
  const { i18n } = useI18n();

  const isHandy = window.innerWidth <= 600;

  const understoodButton = useButton({
    onClick: onComplete,
  });

  const getContent = () => (
    <>
      <ContentBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="xs"
        justifyContent="center">
        <Icon
          height={isHandy ? '48px' : '64px'}
          width={isHandy ? '48px' : '64px'}
          src={InvoiceLateIcon}
          color="var(--colorBlueGray200)"
        />

        <Heading level={isHandy ? 2 : 1} color="var(--colorWhite)">
          {i18n('License.Unpaid.Title')}
        </Heading>
        <Paragraph level={isHandy ? 4 : 2} color="var(--colorBlueGray200)">
          {isStripeLicense
            ? i18n('License.Unpaid.Subtitle.Stripe')
            : i18n('License.Unpaid.Subtitle')}
        </Paragraph>
      </ContentBox>

      <UnderstoodButton {...understoodButton} size="large">
        {i18n('License.Unpaid.Confirm')}
      </UnderstoodButton>
    </>
  );

  /** If handy - don't display it in a modal */
  if (isHandy) {
    return (
      <HandyWrapper
        fluid
        display="flex"
        flexDirection="column"
        gap="small"
        alignItems="center"
        justifyContent="center"
        padding="small">
        {getContent()}
      </HandyWrapper>
    );
  }

  return (
    <Modal isShowing hide={() => {}} backdropClose={false}>
      <ModalWrapper
        display="flex"
        flexDirection="column"
        gap="medium"
        alignItems="center"
        justifyContent="center"
        padding="3xl"
        marginTop="xs"
        marginBottom="xs">
        {getContent()}
      </ModalWrapper>
    </Modal>
  );
};

export default InvoiceWarning;
